<template>
  <div class="route-design-map-page">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
    ></v-progress-linear>

    <v-layout v-if="!loading" row class="fill-height">
      <!-- ===== LEFT SIDEBAR ===== -->
      <div class="sidebar elevation-3">
        <!-- Route Header -->
        <div class="sidebar-header">
          <div class="d-flex align-center px-3 pt-3 mt-1 pb-2">
            <div class="flex-grow-1" style="min-width:0">
              <div
                class="subheading font-weight-bold white--text text-truncate"
                style="line-height:1.35"
              >
                {{ routeDesign && routeDesign.name }}
              </div>
              <div
                class="caption white--text"
                style="opacity:.8;line-height:1.3"
              >
                {{ (routeDesign && routeDesign.seasonName) || "" }}
                <span v-if="routeDesign && routeDesign.regID">
                  · {{ routeDesign.regID }}</span
                >
              </div>
            </div>
            <div
              class="status-pill ml-2"
              :class="
                'status-pill--' +
                  ((routeDesign && routeDesign.status) || 'Draft').toLowerCase()
              "
            >
              {{ routeDesign && routeDesign.status }}
            </div>
          </div>

          <div class="header-tags-grid px-3 pb-3 pt-1">
            <!-- Row 1: Building + Branch -->
            <div class="d-flex align-center" style="gap:5px;min-width:0">
              <span
                v-if="routeDesign && routeDesign.building"
                class="header-tag header-tag--truncate"
                :title="routeDesign.building"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-office-building</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.building }}</span>
              </span>
              <span
                v-if="routeDesign && routeDesign.branch"
                class="header-tag header-tag--truncate"
                :title="routeDesign.branch"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-source-branch</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.branch }}</span>
              </span>
            </div>
            <!-- Row 2: Grades -->
            <div class="d-flex align-center" style="gap:5px;min-width:0">
              <span
                v-if="routeDesign && routeDesign.grades"
                class="header-tag header-tag--truncate"
                :title="routeDesign.grades"
                style="max-width:100%"
              >
                <v-icon x-small color="white" style="flex-shrink:0"
                  >mdi-school</v-icon
                >
                <span class="header-tag__text">{{ routeDesign.grades }}</span>
              </span>
            </div>
            <!-- Edit icon pinned to right -->
            <button
              type="button"
              class="edit-route-filter-btn"
              @click="openRouteFilterDialog"
              title="Edit route pool filters"
            >
              <v-icon x-small color="white">mdi-pencil</v-icon>
            </button>
          </div>
        </div>

        <!-- Student pool count -->
        <div class="px-3 py-2 grey lighten-4 caption grey--text text--darken-2">
          Student pool: {{ poolStudents.length }} students &nbsp;|&nbsp;
          Assigned: {{ totalAssigned }}
        </div>

        <!-- Buses section -->
        <div class="sidebar-buses pa-2">
          <div class="d-flex align-center px-1 mb-2">
            <div class="subheading font-weight-medium">Buses</div>
            <v-spacer></v-spacer>
            <v-btn small color="success" @click="openAddBusDialog">
              <v-icon left small>mdi-plus</v-icon>Add Bus
            </v-btn>
          </div>

          <div
            v-if="!buses.length"
            class="text-xs-center grey--text pa-4 caption"
          >
            No buses yet. Add the first bus to start designing.
          </div>

          <v-card
            v-for="bus in buses"
            :key="bus.id"
            class="bus-card mb-2"
            :class="{ 'bus-card--active': activeBusId === bus.id }"
            :style="
              activeBusId === bus.id
                ? 'border-left: 4px solid ' + busColor(bus) + ' !important'
                : 'border-left: 4px solid transparent'
            "
            @click="selectBus(bus)"
          >
            <div class="pa-2 d-flex align-center" style="min-height:48px">
              <span
                class="bus-color-dot mr-2 flex-shrink-0"
                :style="{ background: busColor(bus) }"
                title="Bus color"
              ></span>
              <div class="flex-grow-1" style="overflow:hidden">
                <div class="d-flex align-center">
                  <span
                    class="body-2 font-weight-medium"
                    style="white-space:nowrap"
                    >{{ bus.busNumber }}</span
                  >
                  <v-icon
                    v-if="bus.isCompleted"
                    small
                    color="green"
                    class="ml-1"
                    style="flex-shrink:0"
                    title="Completed"
                    >mdi-check-circle</v-icon
                  >
                </div>
                <div
                  class="caption grey--text d-flex align-center"
                  style="line-height:1.3"
                >
                  <span>{{ busStudentCount(bus.id) }} students</span>
                  <span
                    v-if="bus.isCompleted"
                    class="green--text ml-1"
                    style="white-space:nowrap"
                    >· saved</span
                  >
                  <span
                    v-else-if="activeBusId === bus.id"
                    class="success--text ml-1"
                    style="white-space:nowrap"
                    >· assigning…</span
                  >
                </div>
              </div>
              <div
                class="d-flex align-center ml-auto flex-shrink-0"
                style="gap:2px"
              >
                <button
                  type="button"
                  class="bus-action-btn"
                  @click.stop="editBusNumber(bus)"
                  title="Rename bus"
                >
                  <v-icon small color="grey darken-1">mdi-pencil</v-icon>
                </button>
                <button
                  type="button"
                  class="bus-action-btn"
                  @click.stop="confirmDeleteBus(bus)"
                  title="Delete bus"
                >
                  <v-icon small color="error">mdi-delete</v-icon>
                </button>
              </div>
            </div>
          </v-card>
        </div>

        <!-- Selected Students Panel -->
        <div v-if="activeBusId" class="sidebar-students">
          <div
            class="d-flex align-center px-3 py-2 grey lighten-3 selected-students-header"
            style="cursor:pointer"
            @click="studentsExpanded = !studentsExpanded"
          >
            <div class="body-2 font-weight-medium">
              Selected Students
              <v-chip x-small color="success" text-color="white" class="ml-1">{{
                activeBusStudents().length
              }}</v-chip>
            </div>
            <v-spacer></v-spacer>
            <v-icon small class="grey--text">{{
              studentsExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
            }}</v-icon>
          </div>
          <div v-if="studentsExpanded" class="selected-students-list">
            <div
              v-if="!activeBusStudents().length"
              class="caption grey--text pa-3 text-xs-center"
            >
              No students selected for this bus yet
            </div>
            <div
              v-for="s in activeBusStudents()"
              :key="s.studentID"
              class="student-row px-3 py-1"
              style="display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:0 6px"
            >
              <div style="min-width:0">
                <div class="caption font-weight-medium text-truncate">
                  {{ s.fullName }}
                </div>
                <div class="caption grey--text text-truncate">
                  {{
                    s.busAddress1 ||
                      (s.house && s.street ? s.house + " " + s.street : "") ||
                      "—"
                  }}
                </div>
              </div>
              <span
                class="caption grey--text text--darken-1"
                style="white-space:nowrap;text-align:right"
                >{{ s.class }}-{{ s.section }}</span
              >
              <v-btn
                icon
                x-small
                flat
                color="error"
                @click="removeStudent(s.studentID)"
                title="Remove from bus"
                style="margin:0"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Sidebar footer actions -->
        <div class="sidebar-footer pa-2">
          <v-divider class="mb-2"></v-divider>
          <div v-if="activeBusId" class="mb-1">
            <v-btn color="success" block :loading="savingBus" @click="saveBus">
              <v-icon left>{{
                nextIncompleteBus
                  ? "mdi-arrow-right-circle"
                  : "mdi-check-circle"
              }}</v-icon>
              {{ saveBusLabel }}
            </v-btn>
          </div>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                color="success"
                outline
                block
                :loading="savingFinal"
                @click="saveFinal"
                v-on="on"
              >
                <v-icon left>mdi-content-save-all</v-icon>
                Save Route Status
              </v-btn>
            </template>
            <span
              >Saves the overall route status (Partial / Complete). Use this
              after all buses are done.</span
            >
          </v-tooltip>
          <v-btn color="success" outline block class="mt-2" @click="exportCSV">
            <v-icon left>mdi-file-excel</v-icon>
            Export All to CSV
          </v-btn>
        </div>
      </div>

      <!-- ===== MAIN MAP AREA ===== -->
      <div class="map-area">
        <!-- Toolbar row 1: bus status + mode + filters -->
        <div class="map-toolbar elevation-1 px-2 py-1 d-flex align-center">
          <!-- Active bus indicator -->
          <div class="d-flex align-center mr-2" style="min-width:0">
            <span v-if="activeBusId" class="d-flex align-center">
              <span
                class="bus-color-dot mr-1"
                :style="{ background: activeBusColor }"
              ></span>
              <span
                class="caption font-weight-medium"
                style="white-space:nowrap"
                >{{ activeBusNumber }}</span
              >
            </span>
            <span v-else class="caption grey--text" style="white-space:nowrap">
              ← Select a bus
            </span>
          </div>

          <v-divider vertical class="mx-2" style="height:24px"></v-divider>

          <!-- Drawing mode toggle -->
          <div
            class="draw-toggle-group"
            :class="{ 'draw-toggle-group--disabled': !activeBusId }"
            :title="!activeBusId ? 'Select a bus first to enable drawing' : ''"
          >
            <button
              type="button"
              class="draw-toggle-btn"
              :class="{ 'draw-toggle-btn--active': drawingMode === 'LINE' }"
              @click="drawingMode = 'LINE'"
              title="Click on map to trace a street → double-click to finish"
            >
              <v-icon small>mdi-gesture</v-icon><span>Line</span>
            </button>
            <button
              type="button"
              class="draw-toggle-btn"
              :class="{
                'draw-toggle-btn--active': drawingMode === 'RADIUS_LINE',
              }"
              @click="drawingMode = 'RADIUS_LINE'"
              title="Trace an avenue → selects students within radius on both sides"
            >
              <v-icon small>mdi-map-marker-radius</v-icon><span>Radius</span>
            </button>
            <button
              type="button"
              class="draw-toggle-btn"
              :class="{ 'draw-toggle-btn--active': drawingMode === 'BOX' }"
              @click="drawingMode = 'BOX'"
              title="Hold Shift and drag on the map"
            >
              <v-icon small>mdi-select</v-icon><span>Box</span>
            </button>
          </div>

          <!-- Radius input — inline inside toolbar when in RADIUS_LINE mode -->
          <div
            v-if="activeBusId && drawingMode === 'RADIUS_LINE'"
            class="radius-inline-input ml-2"
          >
            <input
              v-model.number="radiusFeet"
              type="number"
              min="50"
              max="5000"
              class="radius-inline-input__field"
              title="Selection radius in feet"
            />
            <span class="radius-inline-input__unit">ft</span>
          </div>

          <!-- Drawing in progress -->
          <div v-if="isDrawingLine" class="d-flex align-center ml-2">
            <v-icon small color="success" class="mr-1">mdi-record</v-icon>
            <span class="caption success--text mr-2"
              >Drawing… dbl-click to finish</span
            >
            <v-btn x-small flat color="error" @click="cancelDrawing">
              <v-icon x-small left>mdi-close</v-icon>Cancel
            </v-btn>
          </div>

          <v-spacer></v-spacer>

          <!-- Overview mode toggle -->
          <v-btn
            small
            :color="overviewMode ? 'success' : 'default'"
            :dark="overviewMode"
            :outline="!overviewMode"
            class="toolbar-action-btn"
            style="margin-right:8px !important"
            @click="toggleOverviewMode"
            title="Show all buses, drawings and assignments at once"
          >
            <v-icon small left>mdi-map-legend</v-icon>
            Overview
          </v-btn>

          <div class="d-flex align-center toolbar-right-actions">
            <v-btn
              small
              outline
              class="toolbar-action-btn"
              @click="clearAllSelections"
              title="Remove all student selections for the active bus"
            >
              <v-icon small left>mdi-eraser</v-icon>
              Clear Bus Selections
            </v-btn>

            <v-btn
              small
              outline
              class="toolbar-action-btn"
              @click="helpDialog = true"
              title="How to use this page"
            >
              <v-icon small left>mdi-help-circle-outline</v-icon>
              Instructions
            </v-btn>
          </div>
        </div>

        <!-- Instruction hint strip -->
        <div v-if="activeBusId" class="instruction-strip">
          <!-- Drawing in progress -->
          <div
            v-if="isDrawingLine"
            class="instruction-strip__row instruction-strip__row--active"
          >
            <v-icon small color="success" class="instruction-strip__icon"
              >mdi-record-circle</v-icon
            >
            <div>
              <div class="instruction-strip__main success--text">
                Drawing in progress
              </div>
              <div class="instruction-strip__sub">
                Keep clicking to add points along the street —
                <strong>double-click</strong> to finish and select students.
              </div>
            </div>
          </div>

          <!-- Line mode -->
          <div
            v-else-if="drawingMode === 'LINE'"
            class="instruction-strip__row"
          >
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-gesture</v-icon
            >
            <div>
              <div class="instruction-strip__main">Line mode</div>
              <div class="instruction-strip__sub">
                Click on the map to trace a street, then
                <strong>double-click</strong> to finish. Students within ~100 ft
                of the line are selected.
              </div>
            </div>
          </div>

          <!-- Radius Line mode -->
          <div
            v-else-if="drawingMode === 'RADIUS_LINE'"
            class="instruction-strip__row"
          >
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-map-marker-radius</v-icon
            >
            <div>
              <div class="instruction-strip__main">
                Radius Line mode &mdash; {{ radiusFeet }} ft radius
              </div>
              <div class="instruction-strip__sub">
                Click along an avenue, then <strong>double-click</strong> to
                finish. Students within {{ radiusFeet }} ft on both sides are
                selected.
              </div>
            </div>
          </div>

          <!-- Box mode -->
          <div v-else-if="drawingMode === 'BOX'" class="instruction-strip__row">
            <v-icon small color="green darken-1" class="instruction-strip__icon"
              >mdi-selection</v-icon
            >
            <div>
              <div class="instruction-strip__main">Box mode</div>
              <div class="instruction-strip__sub">
                Hold <kbd>Shift</kbd> and drag on the map to draw a rectangle —
                all students inside are selected.
              </div>
            </div>
          </div>

          <!-- Tip row (always shown when not drawing) -->
          <div v-if="!isDrawingLine" class="instruction-strip__tip">
            Click any student dot on the map to select or deselect individually.
          </div>
        </div>

        <!-- Map display filter bar (always visible) -->
        <div class="display-filter-bar">
          <span class="display-filter-label">
            <v-icon small color="grey darken-1" style="margin-right:4px"
              >mdi-eye</v-icon
            >
            Show on map:
          </span>
          <div class="display-filter-fields">
            <v-autocomplete
              v-model="filters.building"
              :items="buildingOptions"
              label="Building"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 &&
                      filters.building.length === buildingOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.building.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filters.branch"
              :items="branchOptions"
              label="Branch"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 &&
                      filters.branch.length === branchOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.branch.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filters.class"
              :items="classOptions"
              label="Grade"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 && filters.class.length === classOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.class.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
            <v-autocomplete
              v-model="filters.section"
              :items="sectionOptions"
              label="Section"
              dense
              outlined
              multiple
              clearable
              hide-details
              class="display-filter-field"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="
                    index === 0 &&
                      filters.section.length === sectionOptions.length
                  "
                  class="grey--text caption"
                  >All</span
                >
                <span v-else-if="index === 0" class="caption">{{ item }}</span>
                <span v-if="index === 1" class="grey--text caption ml-1"
                  >(+{{ filters.section.length - 1 }})</span
                >
              </template>
            </v-autocomplete>
          </div>
        </div>

        <!-- Map -->
        <div class="map-container" ref="mapContainer">
          <l-map
            ref="map"
            :zoom="zoom"
            :center="center"
            @ready="onMapReady"
            class="the-map"
          >
            <l-tile-layer
              :url="tileUrl"
              :attribution="attribution"
            ></l-tile-layer>

            <l-marker
              v-for="loc in visibleLocations"
              :key="loc.id"
              :lat-lng="[loc.lat, loc.lng]"
              :icon="markerIcon(loc)"
              @click="onMarkerClick(loc)"
            >
              <l-popup>
                <div style="min-width:180px">
                  <strong>{{ loc.name }}</strong
                  ><br />
                  <span class="grey--text" style="font-size:0.85em"
                    >{{ loc.residents.length }}
                    {{
                      loc.residents.length === 1 ? "student" : "students"
                    }}</span
                  >
                  <span
                    v-if="loc.building"
                    class="grey--text"
                    style="font-size:0.85em"
                  >
                    · Bldg {{ loc.building }}</span
                  >
                  <div
                    v-for="r in loc.residents"
                    :key="r.uniqueId"
                    style="font-size:0.85em;margin-top:4px"
                  >
                    {{ r.fullName }}
                    <span class="grey--text"
                      >· {{ r.class }}-{{ r.section }}</span
                    >
                    <span
                      v-if="studentBusId(r.studentID)"
                      :style="{
                        color: busColorById(studentBusId(r.studentID)),
                      }"
                      class="ml-1"
                    >
                      ● {{ busNumberById(studentBusId(r.studentID)) }}
                    </span>
                  </div>
                </div>
              </l-popup>
            </l-marker>
          </l-map>
        </div>
      </div>
    </v-layout>

    <!-- ADD BUS DIALOG -->
    <v-dialog v-model="addBusDialog" max-width="360">
      <v-card>
        <v-card-title class="title pt-3 pb-1">Add Bus</v-card-title>
        <v-card-text class="pt-2 pb-1">
          <v-text-field
            v-model="newBusNumber"
            label="Bus Number / Name"
            outlined
            dense
            autofocus
            @keyup.enter="doAddBus"
          ></v-text-field>
          <div class="caption grey--text mt-n2">
            Leave blank to auto-name (Bus 1, Bus 2, …)
          </div>
        </v-card-text>
        <v-card-actions class="pt-1 pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="addBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="addingBus" @click="doAddBus"
            >Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- RENAME BUS DIALOG -->
    <v-dialog v-model="renameBusDialog" max-width="380">
      <v-card>
        <v-card-title class="headline">Rename Bus</v-card-title>
        <v-card-text class="py-0">
          <v-text-field
            v-model="renameBusNumber"
            label="Bus Number / Name"
            outlined
            dense
            autofocus
            :error-messages="renameBusDuplicateError"
            @keyup.enter="doRenameBus"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="renameBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="renamingBus"
            :disabled="!!renameBusDuplicateError"
            @click="doRenameBus"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EDIT ROUTE FILTERS DIALOG -->
    <v-dialog v-model="routeFilterDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="title pt-3 pb-1">
          <v-icon left color="success">mdi-filter</v-icon>
          Edit Route Pool Filters
        </v-card-title>
        <v-card-text class="pt-2">
          <div class="caption grey--text mb-3">
            These filters define which students appear as dots on the map. If
            your new filters would exclude a student who is
            <strong>already assigned to a bus</strong>, you'll be asked whether
            to keep or remove that assignment — nothing changes automatically.
          </div>
          <v-autocomplete
            v-model="editFilter.buildingList"
            :items="allBuildingOptions"
            label="Building(s)"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            class="mb-1"
          ></v-autocomplete>
          <v-autocomplete
            v-model="editFilter.branchList"
            :items="allBranchOptions"
            label="Branch(es)"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            class="mb-1"
          ></v-autocomplete>
          <v-autocomplete
            v-model="editFilter.gradeList"
            :items="allGradeOptions"
            label="Grades"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            clearable
          ></v-autocomplete>

          <!-- Conflict warning -->
          <v-alert
            :value="routeFilterConflict.length > 0"
            type="warning"
            class="mt-2"
            outline
          >
            <strong>{{ routeFilterConflict.length }} student(s)</strong>
            are already assigned to a bus but would no longer match the new
            filters. Applying will remove them from their assigned bus.
          </v-alert>
        </v-card-text>
        <v-card-actions class="pt-0 pb-3 px-0">
          <v-spacer></v-spacer>
          <v-btn
            color="default"
            dark
            @click="
              routeFilterDialog = false;
              routeFilterConflict = [];
            "
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="routeFilterSaving"
            @click="checkRouteFilterConflicts"
          >
            <v-icon left small>mdi-check</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- HELP / INSTRUCTIONS DIALOG -->
    <v-dialog v-model="helpDialog" max-width="620" scrollable>
      <v-card>
        <v-card-title class="help-dialog-title py-0">
          <v-icon left color="white">mdi-map-legend</v-icon>
          How to Design a Route
          <v-spacer></v-spacer>
          <v-btn class="my-0" icon flat small @click="helpDialog = false">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4 pb-2" style="max-height:520px;overflow-y:auto">
          <!-- Step 1 -->
          <div class="help-step">
            <div class="help-step-num">1</div>
            <div class="help-step-body">
              <div class="help-step-title">Overview Mode (Starting View)</div>
              <div class="help-step-desc">
                When you open a route, the map starts in <strong>Overview</strong> mode —
                all buses' drawings and student assignments are visible at once, each colored by bus.
                Unassigned students appear as small red dots. Use the
                <strong>Overview</strong> button in the toolbar to toggle this view at any time.
              </div>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="help-step">
            <div class="help-step-num">2</div>
            <div class="help-step-body">
              <div class="help-step-title">Select a Bus to Work On</div>
              <div class="help-step-desc">
                Click any bus in the left panel to make it active. This exits Overview mode
                and focuses the map on that bus — only its drawings and students are shown.
                If you have unsaved changes on the current bus, you'll be prompted to
                save or discard before switching.
              </div>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="help-step">
            <div class="help-step-num">3</div>
            <div class="help-step-body">
              <div class="help-step-title">Choose a Drawing Tool</div>
              <div class="help-step-desc">
                Three tools are available in the toolbar:
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-gesture</v-icon>
                <div>
                  <strong>Line</strong> — Click along a street to trace it, then
                  <strong>double-click</strong> to finish. All students within ~100 ft of the line are selected.
                </div>
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-map-marker-radius</v-icon>
                <div>
                  <strong>Radius</strong> — Same as Line, but selects students on both sides
                  up to a custom distance (default 500 ft). Useful for avenues with side streets.
                  Set the distance using the <strong>ft</strong> input next to the toolbar buttons.
                </div>
              </div>
              <div class="help-tool-row">
                <v-icon small color="green darken-1" class="mr-2">mdi-selection</v-icon>
                <div>
                  <strong>Box</strong> — Hold <kbd>Shift</kbd> and drag on the map to draw a rectangle.
                  All students inside are selected.
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4 -->
          <div class="help-step">
            <div class="help-step-num">4</div>
            <div class="help-step-body">
              <div class="help-step-title">Select Individual Students</div>
              <div class="help-step-desc">
                Click any student dot on the map to select or deselect them individually —
                regardless of which drawing tool is active. The popup shows the student's
                name, grade, building, and current bus assignment.
              </div>
            </div>
          </div>

          <!-- Step 5 -->
          <div class="help-step">
            <div class="help-step-num">5</div>
            <div class="help-step-body">
              <div class="help-step-title">Save a Bus &amp; Move to the Next</div>
              <div class="help-step-desc">
                When done assigning students to the active bus, click
                <strong>Save &amp; Go to Bus X</strong> at the bottom of the sidebar.
                This saves the bus and switches to the next unfinished one.
                Repeat for each bus. If you switch buses before saving, a prompt will
                give you the option to save first.
              </div>
            </div>
          </div>

          <!-- Step 6 -->
          <div class="help-step">
            <div class="help-step-num">6</div>
            <div class="help-step-body">
              <div class="help-step-title">Add More Buses</div>
              <div class="help-step-desc">
                Use the <strong>+ Add Bus</strong> button in the sidebar to add extra
                buses at any time during the design process.
              </div>
            </div>
          </div>

          <!-- Step 7 -->
          <div class="help-step">
            <div class="help-step-num">7</div>
            <div class="help-step-body">
              <div class="help-step-title">Filter What Shows on the Map</div>
              <div class="help-step-desc">
                The <strong>Show on map</strong> bar lets you narrow which students
                appear as dots — by building, branch, grade, or section. All are shown
                by default; deselect values to hide them. This only affects map visibility,
                not the student pool or assignments.
              </div>
            </div>
          </div>

          <!-- Step 8 -->
          <div class="help-step">
            <div class="help-step-num">8</div>
            <div class="help-step-body">
              <div class="help-step-title">Finalize the Route</div>
              <div class="help-step-desc">
                Once all buses are complete, click <strong>Save Route Status</strong>
                to update the route's status. Use <strong>Export All to CSV</strong>
                to download the full list of student assignments across all buses.
              </div>
            </div>
          </div>

          <div class="help-tip mt-3 mb-2">
            <v-icon small color="blue darken-1" class="mr-1">mdi-lightbulb-outline</v-icon>
            <span>
              Use <strong>Clear Bus Selections</strong> in the toolbar to reset all student
              assignments for the active bus. Use the pencil icon in the sidebar header to
              update the route's pool filters (building, branch, grades) at any time.
            </span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="helpDialog = false">Got it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- UNSAVED SWITCH CONFIRM DIALOG -->
    <v-dialog v-model="switchBusConfirmDialog" max-width="360" persistent>
      <v-card>
        <v-card-title class="title pt-3 pb-1">
          <v-icon left color="warning">mdi-alert</v-icon>
          Unsaved Changes
        </v-card-title>
        <v-card-text class="pt-2" style="font-size:14px;color:#333;line-height:1.6">
          This bus has student assignments that haven't been saved yet.
          What would you like to do?
        </v-card-text>
        <div class="px-4 pb-4" style="display:flex;flex-direction:column;gap:4px">
          <v-btn class="mb-0" color="success" block :loading="savingBus" @click="saveAndSwitch">
            <v-icon small left>mdi-content-save</v-icon>Save &amp; Switch Bus
          </v-btn>
          <v-btn class="mb-0"color="warning" block outline @click="confirmSwitchBus">
            Switch Without Saving
          </v-btn>
          <v-btn class="mb-0" color="default" dark block @click="switchBusConfirmDialog = false; pendingSwitchBus = null">
            Stay Here
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- DELETE BUS DIALOG -->
    <v-dialog v-model="deleteBusDialog" max-width="380">
      <v-card>
        <div class="delete-dialog-header">
          <v-icon color="error" size="36">mdi-alert-circle-outline</v-icon>
        </div>
        <v-card-title class="delete-dialog-title">Delete Bus?</v-card-title>
        <v-card-text class="text-xs-center pb-1">
          <div class="delete-bus-name">{{ deleteBusTarget && deleteBusTarget.busNumber }}</div>
          <div class="mt-2" style="font-size:14px;color:#333;line-height:1.5">
            This will permanently remove this bus, including all its
            <span style="font-weight:700;color:#c62828">{{ busStudentCount(deleteBusTarget && deleteBusTarget.id) }} student assignment(s)</span>
            and drawings.
          </div>
        </v-card-text>
        <v-card-actions class="pt-2 pb-4 px-2">
          <v-btn
            color="default"
            dark
            block
            @click="deleteBusDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            block
            :loading="deletingBus"
            @click="doDeleteBus"
          >
            <v-icon left small>mdi-delete</v-icon>Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- FILTER CONFLICT DIALOG -->
    <v-dialog v-model="filterConflictDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline">Filter Change Warning</v-card-title>
        <v-card-text>
          Changing filters will hide
          <strong>{{ filterConflictCount }}</strong> student(s) that are already
          selected for <strong>{{ activeBusNumber }}</strong
          >. Their assignments won't be lost — they'll still be saved — but they
          won't be visible on the map.
        </v-card-text>
        <v-card-actions class="pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelFilterChange"
            >Keep current filters</v-btn
          >
          <v-btn color="success" outline @click="applyFilterChange"
            >Apply filters anyway</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- FINAL SAVE DIALOG -->
    <v-dialog v-model="finalSaveDialog" max-width="440">
      <v-card>
        <v-card-title class="headline">Save Route Design</v-card-title>
        <v-card-text>
          <div class="mb-3">
            <strong>{{ completedBusCount }}</strong> of
            <strong>{{ buses.length }}</strong> buses are marked complete.
          </div>
          <v-select
            v-model="finalStatus"
            :items="['Partial', 'Complete']"
            label="Set Status"
            outlined
            dense
          ></v-select>
        </v-card-text>
        <v-card-actions class="pb-3 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="finalSaveDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" :loading="savingFinal" @click="doFinalSave"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top :timeout="3000">
      {{ snackbarText }}
      <v-btn dark flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <v-alert
      :value="!!errorMessage"
      type="error"
      @input="errorMessage = ''"
      class="ma-3"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const BUS_COLORS = [
  "#1565C0",
  "#2E7D32",
  "#E65100",
  "#6A1B9A",
  "#00695C",
  "#AD1457",
  "#F57F17",
  "#0277BD",
  "#4527A0",
  "#37474F",
  "#BF360C",
  "#1B5E20",
];
const FEET_TO_METERS = 0.3048;
const LINE_DEFAULT_RADIUS_FEET = 100;

function createCircleIcon(size, color, opacity) {
  const r = size / 2;
  const sw = 1.5;
  const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
    <circle cx="${r}" cy="${r}" r="${r -
    sw}" fill="${color}" fill-opacity="${opacity}" stroke="#fff" stroke-width="${sw}"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: "custom-circle-marker",
  });
}

function distanceToSegment(p, a, b) {
  const dx = b.lat - a.lat;
  const dy = b.lng - a.lng;
  if (dx === 0 && dy === 0) {
    return p.distanceTo(L.latLng(a.lat, a.lng));
  }
  const t = Math.max(
    0,
    Math.min(
      1,
      ((p.lat - a.lat) * dx + (p.lng - a.lng) * dy) / (dx * dx + dy * dy)
    )
  );
  return p.distanceTo(L.latLng(a.lat + t * dx, a.lng + t * dy));
}

function distanceToPolyline(lat, lng, points) {
  const p = L.latLng(lat, lng);
  let min = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const d = distanceToSegment(p, points[i], points[i + 1]);
    if (d < min) min = d;
  }
  return min;
}

export default {
  name: "RouteDesignMap",
  components: { LMap, LTileLayer, LMarker, LPopup },
  data() {
    return {
      loading: true,
      routeDesign: null,
      buses: [],
      busStudentMap: {},
      busAssignmentSourceMap: {},
      busDrawingMap: {},

      students: [],
      addressInfos: [],

      activeBusId: null,

      drawingMode: "LINE",
      radiusFeet: 500,

      isDrawingLine: false,
      drawingPoints: [],
      currentPolyline: null,
      previewPolyline: null,

      rectangle: null,
      startLatLng: null,

      map: null,
      drawnLayers: {},

      filters: { building: [], branch: [], class: [], section: [] },

      routeFilterDialog: false,
      helpDialog: false,
      editFilter: { building: "", branch: "", grades: "" },
      routeFilterSaving: false,
      routeFilterConflict: [],

      pendingFilters: null,
      filterConflictDialog: false,
      filterConflictCount: 0,

      addBusDialog: false,
      newBusNumber: "",
      addingBus: false,

      renameBusDialog: false,
      renameBusTarget: null,
      renameBusNumber: "",
      renamingBus: false,

      deleteBusDialog: false,
      deleteBusTarget: null,
      deletingBus: false,

      savingBus: false,
      finalSaveDialog: false,
      finalStatus: "Partial",
      savingFinal: false,

      zoom: 13,
      center: [40.7128, -73.95],
      tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: "&copy; OpenStreetMap contributors",

      studentsExpanded: true,
      _clickTimer: null,

      // Track saved snapshot per bus so we can detect unsaved changes
      savedBusStudentSnapshot: {}, // busId → serialized student list at last save

      // Unsaved-switch confirmation
      switchBusConfirmDialog: false,
      pendingSwitchBus: null,

      // Overview mode: show all buses at once
      overviewMode: true,

      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      errorMessage: "",
    };
  },
  computed: {
    statusChipColor() {
      if (!this.routeDesign) return "blue-grey";
      if (this.routeDesign.status === "Complete") return "green darken-1";
      if (this.routeDesign.status === "Partial") return "orange darken-2";
      return "blue-grey";
    },
    poolStudents() {
      if (!this.routeDesign) return this.students;
      const rd = this.routeDesign;
      const buildingList = rd.building
        ? rd.building.split(",").map((v) => v.trim())
        : null;
      const branchList = rd.branch
        ? rd.branch.split(",").map((v) => v.trim())
        : null;
      const gradeList = rd.grades
        ? rd.grades.split(",").map((v) => v.trim())
        : null;
      const result = this.students.filter((s) => {
        if (buildingList && !buildingList.includes(s.building)) return false;
        if (branchList && !branchList.includes(s.branch)) return false;
        if (gradeList && !gradeList.includes(s.class)) return false;
        return true;
      });
      return result;
    },
    filteredStudents() {
      const allBuildings = this.buildingOptions;
      const allBranches = this.branchOptions;
      const allClasses = this.classOptions;
      const allSections = this.sectionOptions;
      return this.poolStudents.filter((s) => {
        const bldgOk =
          this.filters.building.length === 0 ||
          this.filters.building.length === allBuildings.length ||
          this.filters.building.includes(s.building);
        const branchOk =
          this.filters.branch.length === 0 ||
          this.filters.branch.length === allBranches.length ||
          this.filters.branch.includes(s.branch);
        const classOk =
          this.filters.class.length === 0 ||
          this.filters.class.length === allClasses.length ||
          this.filters.class.includes(s.class);
        const sectionOk =
          this.filters.section.length === 0 ||
          this.filters.section.length === allSections.length ||
          this.filters.section.includes(s.section);
        return bldgOk && branchOk && classOk && sectionOk;
      });
    },
    visibleLocations() {
      const grouped = {};
      this.filteredStudents.forEach((s, i) => {
        const key = (s.busHouse || "") + "|" + (s.busStreet || "");
        if (!grouped[key]) {
          grouped[key] = {
            house: s.busHouse,
            street: s.busStreet,
            residents: [],
            building: s.building,
          };
        }
        grouped[key].residents.push(s);
      });

      // Build set of student IDs assigned to OTHER buses (not the active bus)
      const otherBusStudentIds = new Set();
      if (this.activeBusId) {
        Object.keys(this.busStudentMap).forEach((busId) => {
          if (parseInt(busId) !== this.activeBusId) {
            this.busStudentMap[busId].forEach((id) =>
              otherBusStudentIds.add(id)
            );
          }
        });
      }

      const result = [];
      Object.values(grouped).forEach((g) => {
        const info = this.addressInfos.find(
          (a) => a.house === g.house && a.street === g.street
        );
        if (!info) return;

        // In overview mode show everyone; in active-bus mode hide students on other buses
        const availableResidents =
          this.overviewMode || !this.activeBusId
            ? g.residents
            : g.residents.filter((r) => !otherBusStudentIds.has(r.studentID));

        if (!availableResidents.length) return;

        result.push({
          id: info.id,
          name: (g.house || "") + " " + (g.street || ""),
          lat: parseFloat(info.busAddressLatitude),
          lng: parseFloat(info.busAddressLongitude),
          residents: availableResidents,
          building: g.building,
        });
      });
      return result;
    },
    // Filter dropdown options — derived from current pool so user only sees relevant choices
    _sortNs() {
      return (a, b) =>
        String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        });
    },
    buildingOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.building).filter(Boolean)),
      ].sort(this._sortNs);
    },
    branchOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.branch).filter(Boolean)),
      ].sort(this._sortNs);
    },
    classOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.class).filter(Boolean)),
      ].sort(this._sortNs);
    },
    sectionOptions() {
      return [
        ...new Set(this.poolStudents.map((s) => s.section).filter(Boolean)),
      ].sort(this._sortNs);
    },
    // All-student options for the edit-route-filter dialog
    allBuildingOptions() {
      return [
        ...new Set(this.students.map((s) => s.building).filter(Boolean)),
      ].sort(this._sortNs);
    },
    allBranchOptions() {
      return [
        ...new Set(this.students.map((s) => s.branch).filter(Boolean)),
      ].sort(this._sortNs);
    },
    allGradeOptions() {
      return [
        ...new Set(this.students.map((s) => s.class).filter(Boolean)),
      ].sort(this._sortNs);
    },
    activeBusColor() {
      const bus = this.buses.find((b) => b.id === this.activeBusId);
      return bus ? this.busColor(bus) : "#999";
    },
    activeBusNumber() {
      const bus = this.buses.find((b) => b.id === this.activeBusId);
      return bus ? bus.busNumber : "";
    },
    totalAssigned() {
      let total = 0;
      Object.values(this.busStudentMap).forEach((ids) => {
        total += ids.length;
      });
      return total;
    },
    completedBusCount() {
      return this.buses.filter((b) => b.isCompleted).length;
    },
    renameBusDuplicateError() {
      if (!this.renameBusTarget || !this.renameBusNumber.trim()) return "";
      const trimmed = this.renameBusNumber.trim().toLowerCase();
      const isDuplicate = this.buses.some(
        (b) =>
          b.id !== this.renameBusTarget.id &&
          b.busNumber.trim().toLowerCase() === trimmed
      );
      return isDuplicate ? "A bus with this name already exists in this route." : "";
    },
    nextIncompleteBus() {
      return (
        this.buses.find((b) => !b.isCompleted && b.id !== this.activeBusId) ||
        null
      );
    },
    saveBusLabel() {
      if (this.nextIncompleteBus)
        return "Save & Go to " + this.nextIncompleteBus.busNumber;
      return "Save & Mark Complete";
    },
  },
  watch: {
    editFilter: {
      deep: true,
      handler() {
        this.routeFilterConflict = [];
      },
    },
    // When pool options load/change, pre-select all so user deselects to hide
    buildingOptions(newVal) {
      this.filters.building = [...newVal];
    },
    branchOptions(newVal) {
      this.filters.branch = [...newVal];
    },
    classOptions(newVal) {
      this.filters.class = [...newVal];
    },
    sectionOptions(newVal) {
      this.filters.section = [...newVal];
    },
    drawingMode() {
      this.updateMapCursor();
    },
    activeBusId() {
      this.updateMapCursor();
    },
    filters: {
      handler(newVal) {
        if (!this.activeBusId) return;
        const currentIds = this.busStudentMap[this.activeBusId] || [];
        if (!currentIds.length) return;
        const filteredIds = this.filteredStudents.map((s) => s.studentID);
        const hidden = currentIds.filter((id) => !filteredIds.includes(id));
        if (hidden.length) {
          this.filterConflictCount = hidden.length;
          this.pendingFilters = JSON.parse(JSON.stringify(newVal));
          this.filterConflictDialog = true;
        }
      },
      deep: true,
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const [designData, students, addressInfos, seasons] = await Promise.all(
          [
            this.$http.get("api/routeDesign/getRouteDesignByID?id=" + id),
            this.$http.get("api/geoStudents/getStudents"),
            this.$http.get("api/geoStudents/getAddressInfos"),
            this.$http.get("api/route/getRouteSeasons"),
          ]
        );

        const design = designData.design || {};
        // Resolve seasonName from the seasons list (use loose == to handle int/string mismatch)
        const matchedSeason = (seasons || []).find(
          (s) => s.id == design.seasonID || s.ID == design.seasonID
        );
        const seasonName = matchedSeason
          ? matchedSeason.season ||
            matchedSeason.Season ||
            matchedSeason.seasonName ||
            ""
          : "";
        this.routeDesign = { ...design, seasonName };
        this.buses = (designData.buses || []).map((b, i) => ({
          ...b,
          _colorIndex: i,
        }));
        this.addressInfos = addressInfos || [];

        this.students = (students || []).map((s, i) => ({
          ...s,
          studentID: (s.studentID || "").trim(), // normalize — API returns trailing spaces
          fullName:
            ((s.hebName || "") + " " + (s.lastNameH || "")).trim() || s.engName,
          uniqueId: ((s.studentID || "").trim() || "u") + "-" + i,
        }));

        // Rebuild busStudentMap from saved data — use $set for Vue 2 reactivity
        this.busStudentMap = {};
        this.busAssignmentSourceMap = {};
        (designData.students || []).forEach((bs) => {
          const bid = bs.routeDesignBusID;
          const sid = (bs.studentID || "").trim(); // normalize saved IDs too
          if (!this.busStudentMap[bid]) {
            this.$set(this.busStudentMap, bid, []);
            this.$set(this.busAssignmentSourceMap, bid, {});
          }
          this.busStudentMap[bid].push(sid);
          this.$set(this.busAssignmentSourceMap[bid], sid, bs.assignmentSource);
        });

        // Rebuild busDrawingMap — use $set for Vue 2 reactivity
        this.busDrawingMap = {};
        (designData.drawings || []).forEach((d) => {
          const bid = d.routeDesignBusID;
          if (!this.busDrawingMap[bid]) {
            this.$set(this.busDrawingMap, bid, []);
          }
          this.busDrawingMap[bid].push(d);
        });

        // Snapshot initial saved state for all buses
        this.savedBusStudentSnapshot = {};
        Object.keys(this.busStudentMap).forEach((busId) => {
          this.savedBusStudentSnapshot[busId] = JSON.stringify(
            (this.busStudentMap[busId] || []).slice().sort()
          );
        });

        // Start in overview mode (all buses visible); no active bus selected
        this.overviewMode = true;
        this.activeBusId = null;
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to load route design data";
      } finally {
        this.loading = false;
      }
    },
    openRouteFilterDialog() {
      const rd = this.routeDesign || {};
      this.editFilter = {
        buildingList: rd.building
          ? rd.building.split(",").map((v) => v.trim())
          : [],
        branchList: rd.branch ? rd.branch.split(",").map((v) => v.trim()) : [],
        gradeList: rd.grades ? rd.grades.split(",").map((v) => v.trim()) : [],
      };
      this.routeFilterConflict = [];
      this.routeFilterDialog = true;
    },
    checkRouteFilterConflicts() {
      // If warning was already shown, user confirmed — proceed
      if (this.routeFilterConflict.length > 0) {
        this.applyRouteFilter();
        return;
      }

      const ef = this.editFilter;
      const newBuildingList = ef.buildingList.length ? ef.buildingList : null;
      const newBranchList = ef.branchList.length ? ef.branchList : null;
      const newGradeList = ef.gradeList.length ? ef.gradeList : null;

      // Find all currently assigned students that would fall outside new pool
      const conflicts = [];
      Object.keys(this.busStudentMap).forEach((busId) => {
        const ids = this.busStudentMap[busId] || [];
        ids.forEach((sid) => {
          const s = this.students.find((st) => st.studentID === sid);
          if (!s) return;
          const passes =
            (!newBuildingList || newBuildingList.includes(s.building)) &&
            (!newBranchList || newBranchList.includes(s.branch)) &&
            (!newGradeList || newGradeList.includes(s.class));
          if (!passes) conflicts.push({ busId, sid });
        });
      });

      if (conflicts.length === 0) {
        this.applyRouteFilter();
      } else {
        this.routeFilterConflict = conflicts;
      }
    },
    async applyRouteFilter() {
      // If conflicts were shown and user clicked Apply again, proceed and remove them
      if (this.routeFilterConflict.length) {
        this.routeFilterConflict.forEach(({ busId, sid }) => {
          const ids = this.busStudentMap[busId];
          if (ids) {
            const idx = ids.indexOf(sid);
            if (idx > -1) ids.splice(idx, 1);
          }
        });
      }
      this.routeFilterSaving = true;
      try {
        const ef = this.editFilter;
        const newBuilding = ef.buildingList.join(",") || null;
        const newBranch = ef.branchList.join(",") || null;
        const newGrades = ef.gradeList.join(",") || null;

        // Save updated route design
        const payload = {
          ...this.routeDesign,
          building: newBuilding,
          branch: newBranch,
          grades: newGrades,
          updatedAt: new Date().toISOString(),
        };
        await this.$http.post("api/routeDesign/saveRouteDesign", payload);
        this.routeDesign = {
          ...this.routeDesign,
          building: newBuilding,
          branch: newBranch,
          grades: newGrades,
        };

        this.routeFilterDialog = false;
        this.routeFilterConflict = [];
        this.showSnackbar("Route filters updated.");
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to update route filters", "error");
      } finally {
        this.routeFilterSaving = false;
      }
    },
    goBack() {
      this.$router.push({ name: "RouteDesigns" });
    },

    // ── Bus color ──
    busColor(bus) {
      return BUS_COLORS[bus._colorIndex % BUS_COLORS.length];
    },
    busColorById(busId) {
      const bus = this.buses.find((b) => b.id === busId);
      return bus ? this.busColor(bus) : "#999";
    },
    busNumberById(busId) {
      const bus = this.buses.find((b) => b.id === busId);
      return bus ? bus.busNumber : "";
    },
    busStudentCount(busId) {
      return busId && this.busStudentMap[busId]
        ? this.busStudentMap[busId].length
        : 0;
    },
    activeBusStudents() {
      if (!this.activeBusId) return [];
      const ids = this.busStudentMap[this.activeBusId] || [];
      return ids
        .map((id) => this.students.find((s) => s.studentID === id))
        .filter(Boolean);
    },
    studentBusId(studentID) {
      for (const busId in this.busStudentMap) {
        if (this.busStudentMap[busId].includes(studentID))
          return parseInt(busId);
      }
      return null;
    },

    // ── Marker icons ──
    markerIcon(loc) {
      if (!loc.residents || !loc.residents.length)
        return createCircleIcon(10, "#9E9E9E", 0.8);

      if (this.overviewMode) {
        // Show each location colored by its bus assignment; unassigned = red
        for (const b of this.buses) {
          const ids = this.busStudentMap[b.id] || [];
          if (loc.residents.some((r) => ids.includes(r.studentID))) {
            return createCircleIcon(12, this.busColor(b), 1);
          }
        }
        return createCircleIcon(9, "#B71C1C", 0.65);
      }

      // Active-bus mode
      if (this.activeBusId) {
        const activeIds = this.busStudentMap[this.activeBusId] || [];
        const hasActive = loc.residents.some((r) =>
          activeIds.includes(r.studentID)
        );
        if (hasActive) return createCircleIcon(14, this.activeBusColor, 1);
      }

      for (const b of this.buses) {
        const ids = this.busStudentMap[b.id] || [];
        if (loc.residents.some((r) => ids.includes(r.studentID))) {
          return createCircleIcon(11, this.busColor(b), 0.85);
        }
      }

      return createCircleIcon(9, "#B71C1C", 0.65);
    },

    // ── Select bus ──
    selectBus(bus) {
      if (bus.id === this.activeBusId) return;
      if (this.isDrawingLine) this.cancelDrawing();

      // Check if current bus has unsaved changes
      if (this.activeBusId && this.hasUnsavedChanges(this.activeBusId)) {
        this.pendingSwitchBus = bus;
        this.switchBusConfirmDialog = true;
        return;
      }

      this.doSelectBus(bus);
    },
    doSelectBus(bus) {
      this.overviewMode = false;
      this.activeBusId = bus.id;
      this.$nextTick(() => this.updateDrawingLayerOpacity());
    },
    hasUnsavedChanges(busId) {
      const current = JSON.stringify((this.busStudentMap[busId] || []).slice().sort());
      const saved = this.savedBusStudentSnapshot[busId] || "[]";
      return current !== saved;
    },
    confirmSwitchBus() {
      this.switchBusConfirmDialog = false;
      const bus = this.pendingSwitchBus;
      this.pendingSwitchBus = null;
      if (bus) this.doSelectBus(bus);
    },
    async saveAndSwitch() {
      const targetBus = this.pendingSwitchBus;
      this.switchBusConfirmDialog = false;
      this.pendingSwitchBus = null;
      await this.saveBus();
      if (targetBus) this.doSelectBus(targetBus);
    },
    toggleOverviewMode() {
      this.overviewMode = !this.overviewMode;
      if (this.overviewMode) {
        this.activeBusId = null;
        if (this.isDrawingLine) this.cancelDrawing();
      }
      this.$nextTick(() => this.updateDrawingLayerOpacity());
    },
    updateDrawingLayerOpacity() {
      this.buses.forEach((b) => {
        const layers = this.drawnLayers[b.id] || [];
        const isActive = b.id === this.activeBusId;
        layers.forEach((l) => {
          if (l.setStyle) {
            if (this.overviewMode) {
              l.setStyle({ opacity: 0.85, fillOpacity: 0.15, weight: 5 });
            } else {
              l.setStyle({
                opacity: isActive ? 1 : 0,
                fillOpacity: isActive ? 0.15 : 0,
                weight: isActive ? 7 : 4,
              });
            }
          }
        });
      });
    },

    // ── Marker click → toggle students for active bus ──
    onMarkerClick(loc) {
      if (!this.activeBusId) {
        this.showSnackbar("Select a bus first", "warning");
        return;
      }
      if (this.isDrawingLine) return;

      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }

      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      const residentIds = loc.residents.map((r) => r.studentID);

      const allSelected = residentIds.every((id) => ids.includes(id));
      if (allSelected) {
        // Deselect all at this location
        residentIds.forEach((id) => {
          const idx = ids.indexOf(id);
          if (idx > -1) ids.splice(idx, 1);
          delete sources[id];
        });
      } else {
        // Select all at this location
        residentIds.forEach((id) => {
          if (!ids.includes(id)) {
            ids.push(id);
            sources[id] = "MANUAL";
          }
        });
      }
      this.$forceUpdate();
    },

    // ── Filter conflict ──
    cancelFilterChange() {
      this.filterConflictDialog = false;
      this.pendingFilters = null;
    },
    applyFilterChange() {
      this.filterConflictDialog = false;
      this.pendingFilters = null;
    },

    // ── MAP READY ──
    onMapReady(map) {
      this.map = map;
      this.$nextTick(() => {
        if (this.visibleLocations.length) {
          const bounds = L.latLngBounds(
            this.visibleLocations.map((l) => [l.lat, l.lng])
          );
          map.fitBounds(bounds, { padding: [50, 50] });
        }
        this.setupBoxSelection();
        this.setupLineDrawing();
        this.restoreDrawings();
        this.updateDrawingLayerOpacity();
        this.updateMapCursor();
      });
    },

    // ── BOX SELECTION (shift+drag) ──
    setupBoxSelection() {
      const map = this.map;
      map.on("mousedown", (e) => {
        if (!e.originalEvent.shiftKey) return;
        if (this.drawingMode !== "BOX") return; // only in BOX mode
        if (!this.activeBusId) {
          this.showSnackbar("Select a bus first", "warning");
          return;
        }
        if (this.rectangle) map.removeLayer(this.rectangle);
        this.startLatLng = e.latlng;
        const busCol = this.busColorById(this.activeBusId) || "#3388ff";
        this.rectangle = L.rectangle([e.latlng, e.latlng], {
          color: busCol,
          weight: 2,
          fillOpacity: 0.15,
          dashArray: "5 5",
        }).addTo(map);
        map.dragging.disable();
      });
      map.on("mousemove", (e) => {
        if (!this.startLatLng || !this.rectangle) return;
        this.rectangle.setBounds(L.latLngBounds(this.startLatLng, e.latlng));
      });
      map.on("mouseup", () => {
        if (!this.rectangle || !this.startLatLng) return;
        if (this.drawingMode === "BOX") {
          const b = this.rectangle.getBounds();
          this.selectLocationsInBounds(b);
        } else {
          map.removeLayer(this.rectangle);
          this.rectangle = null;
        }
        this.startLatLng = null;
        map.dragging.enable();
      });
    },
    selectLocationsInBounds(bounds) {
      if (!this.activeBusId) {
        this.showSnackbar("Select a bus first", "warning");
        return;
      }
      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }
      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      let added = 0;
      this.visibleLocations.forEach((loc) => {
        if (!bounds.contains([loc.lat, loc.lng])) return;
        loc.residents.forEach((r) => {
          if (!ids.includes(r.studentID)) {
            ids.push(r.studentID);
            sources[r.studentID] = "BOX_DRAW";
            added++;
          }
        });
      });
      // Store the rectangle drawing and track in drawnLayers
      if (this.rectangle) {
        if (added > 0) {
          const geoJson = JSON.stringify(this.rectangle.toGeoJSON());
          if (!this.busDrawingMap[this.activeBusId]) {
            this.$set(this.busDrawingMap, this.activeBusId, []);
          }
          this.busDrawingMap[this.activeBusId].push({
            drawingType: "BOX",
            geoJson,
            selectionRadiusFeet: null,
            createdAt: new Date().toISOString(),
          });
          // Track the live rectangle layer so visibility is managed when switching buses
          if (!this.drawnLayers[this.activeBusId]) {
            this.$set(this.drawnLayers, this.activeBusId, []);
          }
          this.drawnLayers[this.activeBusId].push(this.rectangle);
        } else {
          // No students selected — remove the rectangle immediately
          this.map.removeLayer(this.rectangle);
        }
        this.rectangle = null;
      }
      this.$forceUpdate();
      if (added)
        this.showSnackbar(
          added + " student(s) added to " + this.activeBusNumber
        );
    },

    // ── LINE DRAWING ──
    setupLineDrawing() {
      const map = this.map;

      // Debounce single click to avoid dblclick firing 2 click events
      map.on("click", (e) => {
        if (this.drawingMode !== "LINE" && this.drawingMode !== "RADIUS_LINE")
          return;
        if (!this.activeBusId) {
          this.showSnackbar("Select a bus first", "warning");
          return;
        }
        if (e.originalEvent.shiftKey) return;

        clearTimeout(this._clickTimer);
        this._clickTimer = setTimeout(() => {
          if (!this.isDrawingLine) {
            map.doubleClickZoom.disable();
          }
          this.isDrawingLine = true;
          this.drawingPoints.push(e.latlng);

          if (this.currentPolyline) {
            this.currentPolyline.setLatLngs(this.drawingPoints);
          } else {
            this.currentPolyline = L.polyline(this.drawingPoints, {
              color: this.activeBusColor,
              weight: 7,
              opacity: 1,
              dashArray: "10 6",
            }).addTo(map);
          }
        }, 200);
      });

      map.on("mousemove", (e) => {
        if (!this.isDrawingLine || !this.drawingPoints.length) return;
        const pts = [...this.drawingPoints, e.latlng];
        if (this.previewPolyline) {
          this.previewPolyline.setLatLngs(pts);
        } else {
          this.previewPolyline = L.polyline(pts, {
            color: this.activeBusColor,
            weight: 6,
            opacity: 0.7,
            dashArray: "8 6",
          }).addTo(map);
        }
      });

      map.on("dblclick", (e) => {
        clearTimeout(this._clickTimer); // prevent the pending single-click from firing
        if (this.drawingMode !== "LINE" && this.drawingMode !== "RADIUS_LINE")
          return;
        if (!this.isDrawingLine) return;
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
        this.finishLineDrawing();
        map.doubleClickZoom.enable();
      });
    },
    finishLineDrawing() {
      if (!this.isDrawingLine || this.drawingPoints.length < 2) {
        this.cancelDrawing();
        return;
      }

      const radiusMeters =
        this.drawingMode === "RADIUS_LINE"
          ? (this.radiusFeet || 500) * FEET_TO_METERS
          : LINE_DEFAULT_RADIUS_FEET * FEET_TO_METERS;

      if (!this.busStudentMap[this.activeBusId]) {
        this.$set(this.busStudentMap, this.activeBusId, []);
        this.$set(this.busAssignmentSourceMap, this.activeBusId, {});
      }
      const ids = this.busStudentMap[this.activeBusId];
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      let added = 0;

      this.visibleLocations.forEach((loc) => {
        const dist = distanceToPolyline(loc.lat, loc.lng, this.drawingPoints);
        if (dist <= radiusMeters) {
          loc.residents.forEach((r) => {
            if (!ids.includes(r.studentID)) {
              ids.push(r.studentID);
              sources[r.studentID] =
                this.drawingMode === "RADIUS_LINE"
                  ? "RADIUS_DRAW"
                  : "LINE_DRAW";
              added++;
            }
          });
        }
      });

      // Store drawing
      const geoJson = JSON.stringify({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: this.drawingPoints.map((p) => [p.lng, p.lat]),
        },
      });
      if (!this.busDrawingMap[this.activeBusId]) {
        this.$set(this.busDrawingMap, this.activeBusId, []);
      }
      this.busDrawingMap[this.activeBusId].push({
        drawingType: this.drawingMode,
        geoJson,
        selectionRadiusFeet:
          this.drawingMode === "RADIUS_LINE" ? this.radiusFeet : null,
        createdAt: new Date().toISOString(),
      });

      // Keep polyline on map (solid)
      if (this.currentPolyline) {
        this.currentPolyline.setStyle({
          dashArray: null,
          opacity: 1,
          weight: 7,
        });
        if (!this.drawnLayers[this.activeBusId]) {
          this.drawnLayers[this.activeBusId] = [];
        }
        this.drawnLayers[this.activeBusId].push(this.currentPolyline);
      }

      this.cancelDrawing(false);
      this.updateDrawingLayerOpacity();
      this.$forceUpdate();
      if (added) {
        this.showSnackbar(
          added + " student(s) added to " + this.activeBusNumber
        );
      } else {
        this.showSnackbar("No students found near this line", "warning");
      }
    },
    cancelDrawing(removePolyline) {
      clearTimeout(this._clickTimer);
      if (removePolyline !== false && this.currentPolyline && this.map) {
        this.map.removeLayer(this.currentPolyline);
      }
      if (this.previewPolyline && this.map) {
        this.map.removeLayer(this.previewPolyline);
        this.previewPolyline = null;
      }
      this.currentPolyline = null;
      this.drawingPoints = [];
      this.isDrawingLine = false;
      if (this.map) {
        this.map.doubleClickZoom.enable();
        this.updateMapCursor();
      }
    },
    clearAllSelections() {
      if (!this.activeBusId) return;
      this.$set(this.busStudentMap, this.activeBusId, []);
      this.$set(this.busAssignmentSourceMap, this.activeBusId, {});

      // Remove drawn layers for this bus
      if (this.drawnLayers[this.activeBusId] && this.map) {
        this.drawnLayers[this.activeBusId].forEach((l) =>
          this.map.removeLayer(l)
        );
        delete this.drawnLayers[this.activeBusId];
      }
      this.$set(this.busDrawingMap, this.activeBusId, []);
      this.$forceUpdate();
    },

    // ── Restore drawings when loading saved route ──
    restoreDrawings() {
      if (!this.map) return;
      this.buses.forEach((bus, i) => {
        const drawings = this.busDrawingMap[bus.id] || [];
        drawings.forEach((d) => {
          try {
            const geo = JSON.parse(d.geoJson);
            const color = BUS_COLORS[i % BUS_COLORS.length];
            if (d.drawingType === "BOX") {
              const layer = L.geoJSON(geo, {
                style: { color, weight: 2, fillOpacity: 0.1 },
              }).addTo(this.map);
              if (!this.drawnLayers[bus.id]) this.drawnLayers[bus.id] = [];
              this.drawnLayers[bus.id].push(layer);
            } else {
              const coords = geo.geometry && geo.geometry.coordinates;
              if (coords) {
                const latlngs = coords.map((c) => [c[1], c[0]]);
                const layer = L.polyline(latlngs, {
                  color,
                  weight: 7,
                  opacity: 1,
                }).addTo(this.map);
                if (!this.drawnLayers[bus.id]) this.drawnLayers[bus.id] = [];
                this.drawnLayers[bus.id].push(layer);
              }
            }
          } catch (err) {
            console.error("Failed to restore drawing", err);
          }
        });
      });
    },

    // ── Add Bus ──
    openAddBusDialog() {
      this.newBusNumber = "";
      this.addBusDialog = true;
    },
    async doAddBus() {
      this.addingBus = true;
      try {
        const nextNum = this.buses.length + 1;
        const busNumber =
          (this.newBusNumber && this.newBusNumber.trim()) || "Bus " + nextNum;
        const payload = {
          id: 0,
          routeDesignID: parseInt(this.$route.params.id),
          busNumber,
          busID: null,
          sortOrder: this.buses.length,
          isCompleted: false,
          notes: null,
          createdAt: new Date().toISOString(),
        };
        const res = await this.$http.post("api/routeDesign/saveBus", payload);
        const newId = res && res.id;
        const newBus = {
          ...payload,
          id: newId,
          _colorIndex: this.buses.length,
        };
        this.buses.push(newBus);
        this.$set(this.busStudentMap, newId, []);
        this.$set(this.busAssignmentSourceMap, newId, {});
        this.activeBusId = newId;
        this.addBusDialog = false;
        this.showSnackbar('Bus "' + busNumber + '" added');
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to add bus", "error");
      } finally {
        this.addingBus = false;
      }
    },

    // ── Rename Bus ──
    editBusNumber(bus) {
      this.renameBusTarget = bus;
      this.renameBusNumber = bus.busNumber;
      this.renameBusDialog = true;
    },
    async doRenameBus() {
      if (!this.renameBusTarget || !this.renameBusNumber.trim()) return;
      if (this.renameBusDuplicateError) return;
      this.renamingBus = true;
      try {
        const updated = {
          ...this.renameBusTarget,
          busNumber: this.renameBusNumber.trim(),
        };
        await this.$http.post("api/routeDesign/saveBus", updated);
        const idx = this.buses.findIndex(
          (b) => b.id === this.renameBusTarget.id
        );
        if (idx > -1) this.buses[idx].busNumber = this.renameBusNumber.trim();
        this.renameBusDialog = false;
        this.$forceUpdate();
        this.showSnackbar("Bus renamed");
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to rename bus", "error");
      } finally {
        this.renamingBus = false;
      }
    },

    // ── Delete Bus ──
    confirmDeleteBus(bus) {
      this.deleteBusTarget = bus;
      this.deleteBusDialog = true;
    },
    async doDeleteBus() {
      if (!this.deleteBusTarget) return;
      this.deletingBus = true;
      try {
        await this.$http.post(
          "api/routeDesign/deleteBus?busId=" + this.deleteBusTarget.id
        );
        const id = this.deleteBusTarget.id;
        this.buses = this.buses.filter((b) => b.id !== id);

        // Remove drawn layers
        if (this.drawnLayers[id] && this.map) {
          this.drawnLayers[id].forEach((l) => this.map.removeLayer(l));
          delete this.drawnLayers[id];
        }
        delete this.busStudentMap[id];
        delete this.busAssignmentSourceMap[id];
        delete this.busDrawingMap[id];

        if (this.activeBusId === id) {
          const first = this.buses.find((b) => !b.isCompleted);
          this.activeBusId = first
            ? first.id
            : this.buses.length
            ? this.buses[0].id
            : null;
        }
        this.deleteBusDialog = false;
        this.showSnackbar("Bus deleted");
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to delete bus", "error");
      } finally {
        this.deletingBus = false;
      }
    },

    // ── Save Bus ──
    async saveBus() {
      if (!this.activeBusId) return;
      this.savingBus = true;
      try {
        const bus = this.buses.find((b) => b.id === this.activeBusId);
        if (!bus) return;

        // Save students
        const studentIds = this.busStudentMap[this.activeBusId] || [];
        const sources = this.busAssignmentSourceMap[this.activeBusId] || {};
        const studentPayload = {
          routeDesignBusID: this.activeBusId,
          students: studentIds.map((sId) => ({
            id: 0,
            routeDesignBusID: this.activeBusId,
            studentID: sId,
            assignmentSource: sources[sId] || "MANUAL",
            createdAt: new Date().toISOString(),
          })),
        };
        await this.$http.post(
          "api/routeDesign/saveBusStudents",
          studentPayload
        );

        // Save drawings
        const drawings = this.busDrawingMap[this.activeBusId] || [];
        const drawingPayload = {
          routeDesignBusID: this.activeBusId,
          drawings: drawings.map((d) => ({
            id: 0,
            routeDesignBusID: this.activeBusId,
            drawingType: d.drawingType,
            geoJson: d.geoJson,
            selectionRadiusFeet: d.selectionRadiusFeet || null,
            createdAt: d.createdAt || new Date().toISOString(),
          })),
        };
        await this.$http.post(
          "api/routeDesign/saveBusDrawings",
          drawingPayload
        );

        // Mark bus complete
        const updatedBus = { ...bus, isCompleted: true };
        await this.$http.post("api/routeDesign/saveBus", updatedBus);
        bus.isCompleted = true;
        this.$forceUpdate();

        // Snapshot saved state
        this.$set(
          this.savedBusStudentSnapshot,
          this.activeBusId,
          JSON.stringify((this.busStudentMap[this.activeBusId] || []).slice().sort())
        );

        // Move to next incomplete bus
        const nextBus = this.buses.find(
          (b) => !b.isCompleted && b.id !== this.activeBusId
        );
        if (nextBus) {
          this.activeBusId = nextBus.id;
          this.$nextTick(() => this.updateDrawingLayerOpacity());
          this.showSnackbar("Bus saved! Now assigning " + nextBus.busNumber);
        } else {
          this.showSnackbar("Bus saved! All buses are complete.");
          this.activeBusId = null;
          this.$nextTick(() => this.updateDrawingLayerOpacity());
        }
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to save bus", "error");
      } finally {
        this.savingBus = false;
      }
    },

    // ── Final Save ──
    saveFinal() {
      this.finalStatus =
        this.completedBusCount === this.buses.length ? "Complete" : "Partial";
      this.finalSaveDialog = true;
    },
    async doFinalSave() {
      this.savingFinal = true;
      try {
        const payload = {
          ...this.routeDesign,
          status: this.finalStatus,
          updatedAt: new Date().toISOString(),
        };
        await this.$http.post("api/routeDesign/saveRouteDesign", payload);
        this.routeDesign.status = this.finalStatus;
        this.finalSaveDialog = false;
        this.showSnackbar('Route design saved as "' + this.finalStatus + '"');
      } catch (e) {
        console.error(e);
        this.showSnackbar("Failed to save route design", "error");
      } finally {
        this.savingFinal = false;
      }
    },

    removeStudent(studentID) {
      if (!this.activeBusId) return;
      const ids = this.busStudentMap[this.activeBusId];
      if (!ids) return;
      const idx = ids.indexOf(studentID);
      if (idx > -1) ids.splice(idx, 1);
      const sources = this.busAssignmentSourceMap[this.activeBusId];
      if (sources) delete sources[studentID];
      this.$forceUpdate();
    },
    exportCSV() {
      const BOM = "\uFEFF";
      const routeName = (this.routeDesign && this.routeDesign.name) || "";
      const seasonName =
        (this.routeDesign && this.routeDesign.seasonName) || "";
      const rows = [];

      this.buses.forEach((bus) => {
        const studentIds = this.busStudentMap[bus.id] || [];
        const sources = this.busAssignmentSourceMap[bus.id] || {};
        studentIds.forEach((sId) => {
          const s = this.students.find((st) => st.studentID === sId);
          if (!s) return;
          rows.push(
            [
              '"' + routeName.replace(/"/g, '""') + '"',
              '"' + seasonName.replace(/"/g, '""') + '"',
              '"' + bus.busNumber.replace(/"/g, '""') + '"',
              '="' + (s.studentID || "") + '"',
              '"' +
                ((s.hebName || "") + " " + (s.lastNameH || ""))
                  .trim()
                  .replace(/"/g, '""') +
                '"',
              '"' + (s.engName || "").replace(/"/g, '""') + '"',
              '"' + (s.class || "") + '"',
              '"' + (s.section || "") + '"',
              '"' + (s.building || "") + '"',
              '"' + (s.branch || "") + '"',
              '"' + (s.busAddress1 || "") + '"',
              '"' + (sources[sId] || "") + '"',
            ].join(",")
          );
        });
      });

      if (!rows.length) {
        this.showSnackbar("No students assigned yet", "warning");
        return;
      }

      const headers = [
        "Route Name",
        "Season",
        "Bus Number",
        "Student ID",
        "Hebrew Name",
        "English Name",
        "Class",
        "Section",
        "Building",
        "Branch",
        "Bus Address",
        "Assignment Source",
      ].join(",");

      const csv = BOM + headers + "\n" + rows.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        (routeName || "route-design") + "_students.csv"
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    updateMapCursor() {
      if (!this.map) return;
      const c = this.map.getContainer();
      if (
        this.activeBusId &&
        (this.drawingMode === "LINE" || this.drawingMode === "RADIUS_LINE")
      ) {
        c.style.cursor = "crosshair";
      } else {
        c.style.cursor = "";
      }
    },
    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || "success";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.route-design-map-page {
  height: calc(107vh - 64px);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: -100px; /* cancel #core-view padding-bottom */
}
.fill-height {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Sidebar */
.sidebar {
  width: 320px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  overflow: hidden;
  z-index: 1;
}
.sidebar-header {
  flex-shrink: 0;
  background: #388e3c; /* green darken-2 */
}
.status-pill {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  letter-spacing: 0.3px;
}
.status-pill--draft {
  background: #fff;
  color: #388e3c;
  border: 1.5px solid rgba(255, 255, 255, 0.8);
}
.status-pill--partial {
  background: #ff9800;
  color: #fff;
  border: 1.5px solid rgba(255, 255, 255, 0.5);
}
.status-pill--complete {
  background: #1b5e20;
  color: #a5d6a7;
  border: 1.5px solid #a5d6a7;
}
.header-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  max-width: 100%;
}
.header-tag--truncate {
  min-width: 0;
  max-width: 140px;
}
.header-tag__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.sidebar-buses {
  flex: 1;
  min-height: 100px;
  overflow-y: auto;
}
.sidebar-footer {
  flex-shrink: 0;
}

.display-filter-bar {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  padding: 6px 12px;
  gap: 8px;
}
.display-filter-label {
  display: flex;
  align-items: center;
  white-space: nowrap;
  font-size: 12px;
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}
.display-filter-fields {
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;
}
.display-filter-field {
  flex: 1;
  min-width: 0;
  max-width: 200px;
}

.header-tags-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 4px 6px;
  align-items: center;
}
.header-tags-grid > div:first-child {
  grid-column: 1;
  grid-row: 1;
}
.header-tags-grid > div:nth-child(2) {
  grid-column: 1;
  grid-row: 2;
}
.header-tags-grid > button {
  grid-column: 2;
  grid-row: 1 / 3;
  align-self: center;
}
.edit-route-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  opacity: 0.7;
  transition: opacity 0.15s;
}
.edit-route-filter-btn:hover {
  opacity: 1;
}
.delete-dialog-header {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px;
}
.delete-dialog-title {
  justify-content: center !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  padding-top: 0 !important;
  padding-bottom: 4px !important;
}
.delete-bus-name {
  display: inline-block;
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 5px;
  font-weight: 700;
  font-size: 15px;
  padding: 3px 12px;
  margin-bottom: 8px;
}
.bus-card {
  cursor: pointer;
  border-radius: 6px !important;
  transition: background 0.15s;
}
.bus-card:hover {
  background: #f5f5f5;
}
.bus-card--active {
  background: #f0fff4 !important;
}
.bus-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s;
}
.bus-action-btn:hover {
  background: rgba(0, 0, 0, 0.07);
}
.bus-color-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  min-width: 14px;
  max-width: 14px;
  min-height: 14px;
  max-height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  align-self: center;
  border: 2px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Drawing mode toggle (CustomPayment-style) */
.draw-toggle-group {
  display: inline-flex;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 auto;
  width: -webkit-fit-content;
  width: -moz-fit-content;
  max-width: 200px !important;
}
.draw-toggle-group--disabled {
  opacity: 0.4;
  pointer-events: none;
}
.radius-inline-input {
  display: inline-flex;
  align-items: center;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
  height: 32px;
  background: #fff;
  flex: 0 0 auto;
  width: -webkit-fit-content;
  width: -moz-fit-content;
  max-width: 68px !important;
}
.radius-inline-input__field {
  width: 46px;
  border: none;
  outline: none;
  padding: 0 6px;
  font-size: 13px;
  font-weight: 500;
  color: #2e7d32;
  text-align: right;
  background: transparent;
  -moz-appearance: textfield;
  appearance: textfield;
}
.radius-inline-input__field::-webkit-outer-spin-button,
.radius-inline-input__field::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.radius-inline-input__unit {
  padding: 0 8px 0 2px;
  font-size: 12px;
  color: #888;
  background: #f1f8e9;
  height: 100%;
  display: flex;
  align-items: center;
  border-left: 1px solid #c8e6c9;
}
.draw-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  color: #555;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  outline: none;
  white-space: nowrap;
}
.draw-toggle-btn + .draw-toggle-btn {
  border-left: 1px solid #c8e6c9;
}
.draw-toggle-btn:hover {
  background: #f1f8e9;
  color: #2e7d32;
}
.draw-toggle-btn--active {
  background: #4caf50 !important;
  color: #fff !important;
}
.draw-toggle-btn--active .v-icon {
  color: #fff !important;
}

/* Selected students panel */
.sidebar-students {
  flex-shrink: 0;
  border-top: 1px solid #e0e0e0;
  max-height: 280px;
  display: flex;
  flex-direction: column;
}
.selected-students-header {
  flex-shrink: 0;
}
.selected-students-list {
  overflow-y: auto;
  flex: 1;
}
.student-row {
  border-bottom: 1px solid #f0f0f0;
}
.student-row:hover {
  background: #fafafa;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Map area */
.map-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.map-toolbar {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  min-height: 52px;
}
.toolbar-right-actions {
  flex-shrink: 0;
  gap: 8px;
}
.toolbar-action-btn {
  min-width: 0 !important;
  width: auto !important;
  margin: 0 !important;
  padding-left: 10px !important;
  padding-right: 10px !important;
}
.toolbar-action-btn >>> .v-btn__content {
  padding: 0;
}

/* Help dialog */
.help-dialog-title {
  background: #388e3c;
  color: #fff !important;
  font-size: 16px !important;
  font-weight: 600;
  padding: 14px 16px !important;
}
.help-step {
  display: flex;
  gap: 14px;
  margin-bottom: 18px;
  align-items: flex-start;
}
.help-step-num {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #388e3c;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
}
.help-step-body {
  flex: 1;
}
.help-step-title {
  font-weight: 600;
  font-size: 14px;
  color: #222;
  margin-bottom: 3px;
}
.help-step-desc {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}
.help-tool-row {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}
.help-tip {
  background: #e3f2fd;
  border-left: 3px solid #1976d2;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 12px;
  color: #444;
  display: flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.5;
}
.instruction-strip {
  background: #f1f8e9;
  border-bottom: 1px solid #c8e6c9;
  color: #444;
}
.instruction-strip__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px 4px;
}
.instruction-strip__row--active {
  background: #e8f5e9;
}
.instruction-strip__icon {
  margin-top: 1px;
  flex-shrink: 0;
}
.instruction-strip__main {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
  line-height: 1.3;
}
.instruction-strip__sub {
  font-size: 11px;
  color: #555;
  line-height: 1.4;
  margin-top: 1px;
}
.instruction-strip__tip {
  padding: 2px 12px 7px 36px;
  font-size: 11px;
  color: #888;
  line-height: 1.4;
}
.instruction-strip kbd,
.help-tool-row kbd {
  background: #e8e8e8;
  border: 1px solid #b0b0b0;
  border-bottom-width: 2px;
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 0.78em;
  font-family: monospace;
  color: #333;
  line-height: 1.4;
  display: inline-block;
}
.map-container {
  flex: 1;
  position: relative;
  z-index: 0; /* isolates Leaflet pane z-indexes so Vuetify dialogs appear above */
  overflow: hidden;
}
.the-map {
  width: 100%;
  height: 100%;
}
.custom-circle-marker {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
</style>
